import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { DgNumberPipe } from '../../dg-number.pipe';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import {
  ConfirmedDocumentRequests,
  ConfirmedPackLoanRequests,
  PackagingContainerRequirement,
  PackagingDocumentRequirement,
  PackagingLineRequirement,
  PackLoanLimit,
  PackLoanReasonOption,
  PackLoanRequestPayload,
  PackLoanSuggestion,
} from '../../interfaces/pack-loan-request.interface';
import {
  PACK_LOAN_LIMIT_WARNING,
  PACK_LOAN_MODAL_TEXTS,
} from '../../pack-loan-requests.constants';

// Datos de entrada del modal de solicitudes de vacíos
export interface PackLoanRequestsDialogData {
  documents: PackagingDocumentRequirement[];
  reasons: PackLoanReasonOption[];
  limitExceededBhv: string;
  // etiqueta del documento cuando la clase reparte el pedido
  documentLabels: Record<number, string>;
}

// una fila editable del modal; empaque y contenedor comparten forma
export interface RequestRow {
  key: string;
  documentIndex: number;
  position: number | null;
  title: string;
  subtitle: string;
  suggestion: PackLoanSuggestion;
  limit: PackLoanLimit;
  traceLabel: string;
  selected: boolean;
  quantity: number;
  quantityText: string;
  reasonId: string;
  note: string;
}

export interface DocumentGroup {
  index: number;
  label: string;
  lines: RequestRow[];
  containers: RequestRow[];
}

@Component({
  selector: 'app-pack-loan-requests-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    DgNumberPipe,
  ],
  templateUrl: './pack-loan-requests-modal.component.html',
})
export class PackLoanRequestsModalComponent implements OnInit, OnDestroy {
  readonly texts = PACK_LOAN_MODAL_TEXTS;
  groups: DocumentGroup[] = [];
  private sequence = 0;

  // en pantallas de poca altura (móvil horizontal) el encabezado no deja ver las solicitudes
  shortScreen = false;
  private shortScreenSub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<PackLoanRequestsModalComponent>,
    private breakpointObserver: BreakpointObserver,
    @Inject(MAT_DIALOG_DATA) public data: PackLoanRequestsDialogData,
    private readonly i18n: LanguageService
  ) {
    const defaultReasonId = data.reasons?.length === 1 ? data.reasons[0]._id : '';
    this.groups = (data.documents ?? []).map((document) => ({
      index: document.index,
      label: data.documentLabels?.[document.index] ?? '',
      lines: (document.lines ?? []).map((line) =>
        this.buildLineRow(line, document.index, defaultReasonId)
      ),
      containers: (document.containers ?? []).map((container) =>
        this.buildContainerRow(container, document.index, defaultReasonId)
      ),
    }));
  }

  ngOnInit(): void {
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
  }

  get showsDocumentLabels(): boolean {
    return this.groups.length > 1;
  }

  get blocksOnExcess(): boolean {
    return this.data.limitExceededBhv !== PACK_LOAN_LIMIT_WARNING;
  }

  get selectedRows(): RequestRow[] {
    return this.allRows.filter((row) => row.selected);
  }

  get selectedCount(): number {
    return this.selectedRows.length;
  }

  // el disponible del límite tiene prioridad sobre lo sugerido
  maxFor(row: RequestRow): number {
    const suggested = row.suggestion.systemQuantity;
    if (row.limit.available == null) return suggested;
    return Math.min(suggested, row.limit.available);
  }

  isFree(row: RequestRow): boolean {
    return row.limit.loanLimit == null;
  }

  exceedsLimit(row: RequestRow): boolean {
    if (!row.selected || row.limit.available == null) return false;
    return row.quantity > row.limit.available;
  }

  exceedsSuggestion(row: RequestRow): boolean {
    return row.selected && row.quantity > row.suggestion.systemQuantity;
  }

  hasInvalidQuantity(row: RequestRow): boolean {
    if (!row.selected) return false;
    return row.quantity <= 0 || this.exceedsSuggestion(row);
  }

  missingReason(row: RequestRow): boolean {
    return row.selected && !row.reasonId;
  }

  // dos solicitudes sobre la misma línea no pueden compartir motivo
  duplicatedReason(row: RequestRow): boolean {
    if (!row.selected || !row.reasonId) return false;
    return this.allRows.some(
      (other) =>
        other !== row &&
        other.selected &&
        other.documentIndex === row.documentIndex &&
        other.position === row.position &&
        other.reasonId === row.reasonId
    );
  }

  get hasBlockingErrors(): boolean {
    return this.selectedRows.some(
      (row) =>
        this.hasInvalidQuantity(row) ||
        this.missingReason(row) ||
        this.duplicatedReason(row) ||
        (this.blocksOnExcess && this.exceedsLimit(row))
    );
  }

  get hasWarnings(): boolean {
    return !this.blocksOnExcess && this.selectedRows.some((row) => this.exceedsLimit(row));
  }

  get canConfirm(): boolean {
    return !this.hasBlockingErrors;
  }

  onQuantityInput(row: RequestRow, value: string): void {
    row.quantityText = value ?? '';
    const parsed = Number(String(value).replace(',', '.'));
    row.quantity = Number.isFinite(parsed) ? parsed : 0;
  }

  onToggle(row: RequestRow): void {
    if (!row.selected) return;
    if (row.quantity > 0) return;
    row.quantity = this.maxFor(row);
    row.quantityText = String(row.quantity);
  }

  // una línea puede llevar varias solicitudes si cada una tiene motivo distinto
  addRequestFor(group: DocumentGroup, row: RequestRow): void {
    const copy: RequestRow = {
      ...row,
      key: `${row.key}:extra:${++this.sequence}`,
      selected: true,
      quantity: 0,
      quantityText: '',
      reasonId: '',
      note: '',
    };
    const target = row.position == null ? group.containers : group.lines;
    target.splice(target.indexOf(row) + 1, 0, copy);
  }

  removeRequest(group: DocumentGroup, row: RequestRow): void {
    const target = row.position == null ? group.containers : group.lines;
    target.splice(target.indexOf(row), 1);
  }

  isExtra(row: RequestRow): boolean {
    return row.key.includes(':extra:');
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.canConfirm) return;
    const byDocument = new Map<number, ConfirmedDocumentRequests>();

    for (const group of this.groups) {
      const entry: ConfirmedDocumentRequests = {
        containers: [],
        linesByPosition: new Map<number, PackLoanRequestPayload[]>(),
      };

      for (const row of group.lines) {
        if (!row.selected) continue;
        const payload = this.toPayload(row);
        if (!payload) continue;
        const current = entry.linesByPosition.get(row.position) ?? [];
        current.push(payload);
        entry.linesByPosition.set(row.position, current);
      }

      for (const row of group.containers) {
        if (!row.selected) continue;
        const payload = this.toPayload(row);
        if (payload) entry.containers.push(payload);
      }

      byDocument.set(group.index, entry);
    }

    const result: ConfirmedPackLoanRequests = { byDocument };
    this.dialogRef.close(result);
  }

  private get allRows(): RequestRow[] {
    return this.groups.flatMap((group) => [...group.lines, ...group.containers]);
  }

  private toPayload(row: RequestRow): PackLoanRequestPayload | null {
    const reason = this.data.reasons.find((item) => item._id === row.reasonId);
    if (!reason) return null;
    return {
      material: row.suggestion.material,
      baseUnitOfMeasure: row.suggestion.baseUnitOfMeasure,
      systemQuantity: row.suggestion.systemQuantity,
      originalQuantity: row.quantity,
      quantity: row.quantity,
      reason,
      note: row.note?.trim() ? row.note.trim() : null,
      statusId: StatusEnum.ACTIVE,
    };
  }

  private buildLineRow(
    line: PackagingLineRequirement,
    documentIndex: number,
    reasonId: string
  ): RequestRow {
    return {
      key: `line:${line.position}:${line.suggestion.material._id}`,
      documentIndex,
      position: line.position,
      title: line.suggestion.material.name,
      subtitle: `${this.i18n.t(this.texts.linePrefix)} ${line.position} · ${line.sourceMaterialName}`,
      suggestion: line.suggestion,
      limit: line.limit,
      traceLabel: '',
      selected: false,
      quantity: line.suggestion.systemQuantity,
      quantityText: String(line.suggestion.systemQuantity),
      reasonId,
      note: '',
    };
  }

  private buildContainerRow(
    container: PackagingContainerRequirement,
    documentIndex: number,
    reasonId: string
  ): RequestRow {
    return {
      key: `container:${container.suggestion.material._id}:${container.trace.depth}`,
      documentIndex,
      position: null,
      title: container.suggestion.material.name,
      subtitle: this.i18n.t(this.texts.containerSubtitle),
      suggestion: container.suggestion,
      limit: container.limit,
      traceLabel: this.buildTraceLabel(container),
      selected: false,
      quantity: container.suggestion.systemQuantity,
      quantityText: String(container.suggestion.systemQuantity),
      reasonId,
      note: '',
    };
  }

  // explica de dónde salió la cantidad cuando el usuario pregunta
  private buildTraceLabel(container: PackagingContainerRequirement): string {
    const parts: string[] = [];
    if (container.trace.specificationCode) parts.push(container.trace.specificationCode);
    if (container.trace.ruleCode) parts.push(container.trace.ruleCode);
    if (container.trace.maxQuantity) {
      parts.push(`${this.i18n.t(this.texts.capacity)} ${container.trace.maxQuantity}`);
    }
    parts.push(
      this.i18n.t(container.trace.isMixAllowed ? this.texts.mixAllowed : this.texts.mixNotAllowed)
    );
    return parts.join(' · ');
  }
}
